import { Box, Typography, IconButton, Stack } from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteIcon from "@mui/icons-material/Delete";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DownloadIcon from "@mui/icons-material/Download";
import { alpha } from "@mui/material/styles";
import { UploadedFile } from "./types";
import {
  getFileStateLabel,
  getFileStateColor,
  getIconStateColor,
} from "./helpers";

const FileList = ({
  files,
  onRemove,
}: {
  files: (UploadedFile & { progress?: number })[];
  onRemove: (id: string) => void;
}) => {
  return (
    <Box sx={{ mt: 3, mx: "auto", maxHeight: 282, overflowY: "auto" }}>
      {files.map(
        ({
          id,
          file,
          uploadedAt,
          lastModifiedDate,
          progress = 100,
          uploadError,
        }) => (
          <Box
            key={id}
            sx={{
              position: "relative",
              border: "1px solid",
              borderColor: uploadError ? "error.main" : "grey.300",
              borderRadius: 2,
              p: 2,
              mb: 2,
              overflow: "hidden",
              backgroundColor: "background.paper",
            }}
          >
            {progress < 100 && (
              <Box
                role="progressbar"
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={100}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  height: "100%",
                  width: `${progress}%`,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.15),
                  zIndex: 0,
                  transition: "width 0.3s ease",
                }}
              />
            )}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Stack direction="row" spacing={2} alignItems="center" flex={1}>
                <InsertDriveFileIcon
                  color={getIconStateColor(progress, uploadError)}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={file.name}
                  >
                    {file.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {file.name.split(".").pop() || ""}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <DownloadIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  {(file.size / 1024).toFixed(1)} KB
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <AccessTimeIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  Dropped{" "}
                  {uploadedAt.toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <AutoFixHighIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  Last Modified{" "}
                  {lastModifiedDate?.toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
              </Stack>

              <Typography
                variant="caption"
                color={getFileStateColor(progress, uploadError)}
                sx={{
                  px: 1,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: getFileStateColor(progress, uploadError),
                  userSelect: "none",
                }}
              >
                {getFileStateLabel(progress, uploadError)}
              </Typography>

              <IconButton
                aria-label="delete file"
                size="small"
                onClick={() => onRemove(id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        ),
      )}
    </Box>
  );
};

export default FileList;
